<template>
  <div class="plan-container plan-container__user-mgmt">
    <p class="title">Customer Management</p>
    <div class="search-bar field">
      <span class="icon input-icon">
        <i class="fas fa-search"></i>
      </span>
      <input
        type="text"
        class="input has-icon"
        placeholder="Find organisation"
        v-model="searchTerm"
      />
    </div>
    <div class="plan-list">
      <div class="table-wrapper">
        <table class="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Tier</th>
              <th>Licensed Start Date</th>
              <th>Licensed End Date</th>
              <th>Contact Name</th>
              <th>Contact Email</th>
              <th>Contact Access</th>
              <th>Active</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="org in filteredOrganisations"
              :key="org._id"
              @click="editOrg(org)"
            >
              <td>{{ org.name }}</td>
              <td>{{ org.license.tier }}</td>
              <td>{{ org.license.startDate | formatDate }}</td>
              <td>{{ org.license.endDate | formatDate }}</td>
              <td>
                {{
                  org.license.contacts.length
                    ? org.license.contacts[0].name
                    : ''
                }}
              </td>
              <td>
                {{
                  org.license.contacts.length
                    ? org.license.contacts[0].email
                    : ''
                }}
              </td>
              <td>
                {{
                  org.license.contacts.length
                    ? org.license.contacts[0].role
                    : ''
                }}
              </td>
              <td>{{ org.license.active }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="info-row info-row--flush">
        <button class="button" @click="showOrgInfo = true">
          Add Organisations
        </button>
      </div>
    </div>
    <div class="modal" :class="{ 'is-active': showOrgInfo }">
      <div class="modal-background" @click.stop="closeModal()"></div>
      <div class="modal-content">
        <div class="title">{{ modalName }}</div>
        <hr />
        <div class="field">
          <label for="name">Name</label>
          <span class="icon input-icon">
            <i class="far fa-user"></i>
          </span>
          <input
            type="text"
            class="input has-icon"
            v-model="organisation.name"
          />
        </div>
        <div class="field">
          <label for="name">Tier</label>
          <span class="icon input-icon">
            <i class="far fa-building"></i>
          </span>
          <div class="select">
            <select v-model="license.tier">
              <option :value="1">Tier 1</option>
              <option :value="2">Tier 2</option>
            </select>
          </div>
        </div>
        <div class="field">
          <label for="name">Licensed Start Date</label>
          <span class="icon input-icon">
            <i class="far fa-calendar-alt"></i>
          </span>
          <input
            type="date"
            class="input has-icon"
            v-model="license.startDate"
          />
        </div>
        <div class="field">
          <label for="name">Licensed End Date</label>
          <span class="icon input-icon">
            <i class="far fa-calendar-alt"></i>
          </span>
          <input type="date" class="input has-icon" v-model="license.endDate" />
        </div>
        <div class="field field__activate">
          <label for="name">Activate Organisation Now?</label>
          <input
            type="checkbox"
            class="input has-icon"
            v-model="license.active"
          />
        </div>
        <div class="field">
          <button class="button is-gradient" @click="saveOrgDetails">
            Save
          </button>
        </div>
      </div>
    </div>
    <div class="modal is-active" v-if="canAddContacts">
      <div class="modal-background"></div>
      <div class="modal-content">
        <div class="title">Create Organisation Contacts</div>
        <hr />

        <div class="contacts-list" v-for="(contact, i) in contacts" :key="i">
          <div class="field">
            <label for="name">Name</label>
            <span class="icon input-icon">
              <i class="far fa-user"></i>
            </span>
            <input
              type="text"
              class="input has-icon"
              v-model="contact.name"
              @input="isEditing = i"
            />
          </div>
          <div
            class="field__error"
            v-if="contactFormErrors.length && !contacts[i].name"
          >
            Valid name is required
          </div>
          <div class="field">
            <label for="name">Email</label>
            <span class="icon input-icon">
              <i class="far fa-envelope"></i>
            </span>
            <input type="text" class="input has-icon" v-model="contact.email" />
          </div>
          <div
            class="field__error"
            v-if="
              contactFormErrors.length &&
                (!contacts[i].email ||
                  (contacts[i].email && !validEmail(contacts[i].email)))
            "
          >
            Valid email is required {{ !validEmail(contacts[i].email) }}
          </div>
          <div class="field" ref="autocomplete-field">
            <label for="name">Access Level</label>
            <span class="icon input-icon">
              <i class="fas fa-shield-alt"></i>
            </span>

            <div class="select">
              <select v-model="contact.role">
                <option
                  v-for="role in roles"
                  :key="role"
                  @select="setRole(role, i)"
                  >{{ role }}</option
                >
              </select>
            </div>
          </div>
          <div
            class="field__error"
            v-if="contactFormErrors.length && !contacts[i].role"
          >
            Valid role is required
          </div>
          <hr v-if="i !== contacts.length - 1" />
        </div>

        <div class="field">
          <a @click="contacts.push({})" class="link">Add more</a>
          <button class="button is-gradient" @click="save">Save</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import Fuse from 'fuse.js'
import { validateEmail } from '@/utils/regex-patterns'
import { displayWarning } from '@/utils/notification-factory'

export default {
  name: 'OrgManagement',
  data() {
    return {
      canAddContacts: false,
      showOrgInfo: false,
      isEditing: null,
      organisation: {
        name: null
      },
      license: {
        active: false,
        endDate: null,
        startDate: null,
        tier: null
      },
      contacts: [
        {
          name: null,
          role: null,
          email: null
        }
      ],
      contactFormHasError: false,
      contactFormErrors: [],
      roles: ['audit', 'report', 'manage', 'admin'],
      searchTerm: '',
      results: [],
      isTyping: false
    }
  },
  computed: {
    ...mapGetters({
      users: 'organisation/users',
      organisations: 'super/organisations',
      licenses: 'super/licenses'
    }),
    modalName() {
      if (this.organisation._id) return 'Edit ' + this.organisation.name
      return 'Create Organisation'
    },
    filteredOrganisations() {
      const fuse = new Fuse(this.organisations, {
        includeScore: true,
        keys: [
          'name',
          'license.contacts.name',
          'license.contacts.email',
          'license.contacts.role'
        ]
      })
      return this.searchTerm.length < 3
        ? this.organisations
        : fuse.search(this.searchTerm).map(({ item }) => item)
    }
  },
  methods: {
    ...mapActions({
      fetchOrgs: 'super/fetchOrgs',
      fetchLicenses: 'super/fetchLicenses',
      createOrganisation: 'super/createOrganisation',
      editOrganisation: 'super/editOrganisation'
    }),
    editOrg({ license: { startDate, endDate, contacts, ...license }, ...org }) {
      this.organisation = org
      this.license = {
        startDate: startDate ? startDate.slice(0, 10) : '',
        endDate: endDate ? endDate.slice(0, 10) : '',
        ...license
      }
      this.showOrgInfo = true
    },
    closeModal() {
      this.showOrgInfo = false
      this.organisation = {
        name: null
      }
      this.license = {
        active: false,
        endDate: null,
        startDate: null,
        tier: null
      }
    },
    onChange(index) {
      this.isTyping = true
      this.results = this.roles.filter(
        role => role.toLowerCase().indexOf(this.searchTerm.toLowerCase()) > -1
      )
    },
    setRole(role, i) {
      this.contacts[i].role = role
      this.isTyping = false
      this.results = []
    },
    saveOrgDetails() {
      console.log(this.organisation)
      if (this.organisation._id) {
        this.saveOrg()
        this.closeModal()
        return
      }
      const hasLicenseInfo =
        Object.keys(this.license).filter(l => l !== null).length >= 3
      const hasOrgInfo = this.organisation.name !== null
      console.log({ hasLicenseInfo, hasOrgInfo })
      if (hasLicenseInfo && hasOrgInfo) {
        this.showOrgInfo = false
        this.canAddContacts = true
      }
    },
    validEmail(email) {
      return validateEmail(email)
    },
    validateEmptyFields(contacts) {
      const errors = []
      contacts.forEach(contact => {
        for (const key in contact) {
          if (!contact[key]) {
            errors.push(true)
          }
        }
      })
      return errors
    },
    async save() {
      try {
        this.contactFormErrors = this.validateEmptyFields(this.contacts)
        if (!this.contactFormErrors.length) {
          await this.createOrganisation({
            organisation: this.organisation,
            license: this.license,
            contacts: this.contacts
          })
          this.canAddContacts = false
          window.location.reload()
        }
      } catch (err) {
        displayWarning(err)
        console.log({ err })
      }
    },
    async saveOrg() {
      try {
        await this.editOrganisation({
          organisation: this.organisation,
          license: this.license
        })
        window.location.reload()
      } catch (err) {
        console.log({ err })
      }
    }
  },
  async mounted() {
    await this.fetchOrgs()
  }
}
</script>

<style lang="scss" scoped>
@import '@/style/variables.scss';
@import '@/style/components.scss';
@import '@/style/sections.scss';
@import '@/style/modal.scss';

.field {
  display: flex;
  flex-flow: column;
  margin-bottom: 1rem;
  label {
    font-weight: 600;
    font-size: 0.9rem;
  }
  &__activate {
    flex-flow: row nowrap;
    align-items: center;
    justify-content: flex-start;
    height: auto;
    label {
      padding-bottom: 0;
    }
    input[type='checkbox'] {
      height: auto;
      width: auto;
      margin-left: 0.5rem;
    }
  }

  &__error {
    margin-top: -16px;
    margin-bottom: 8px;
    color: $na-color;
  }
}

.activate-field {
  display: flex;
  margin-bottom: 1rem;
}

span.icon {
  bottom: 0rem;
}

.modal-content > .title {
  text-align: center;
  padding: 0;
  margin: 10px 0;
}

hr {
  border: 1px solid $fill-grey;
  width: 100%;
  margin-bottom: 20px;
}

.is-gradient {
  background: $brand-gradient;
}

.autocomplete-list {
  top: 0;
  width: 100%;
  z-index: 1;
  background: white;
  li {
    list-style-type: none;
    margin-bottom: 1rem;
    cursor: pointer;
  }
  li:hover {
    font-weight: 600;
    text-decoration: underline;
  }
}

.autocomplete-field {
  max-height: 64px;
  transition: 300ms max-height ease-in-out;
}

a.link {
  position: relative;
  margin-bottom: 0.5rem;
  cursor: pointer;
  &:before,
  &:after {
    content: '';
    position: absolute;
    bottom: -2px;
    width: 0px;
    height: 2px;
    margin: 5px 0 0;
    transition: all 100ms ease-out;
    transition-duration: 0.75s;
    background-color: darken(#ea5393, 5%);
    left: 0;
  }
  &:hover {
    &::before,
    &::after {
      width: 75px;
    }
  }
}

.select select {
  display: inline-flex;
  outline: 0;
  appearance: none;
  height: 2.5rem;
  background-color: #fff;
  border: 1px solid rgba(0, 0, 0, 0.3);
  border-radius: 99px;
  color: #363636;
  width: 100%;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  padding: 0.25rem 1rem;
  line-height: 1.5;
  padding-left: 3rem;
}
</style>
